const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? "";

export const hasBackend = (): boolean => BASE_URL.length > 0;

function toSnake(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
    const snake = key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`);
    result[snake] = obj[key];
  }
  return result;
}

function toCamel(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(obj)) {
    const camel = key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());
    const val = obj[key];
    result[camel] =
      val && typeof val === "object" && !Array.isArray(val)
        ? toCamel(val as Record<string, unknown>)
        : Array.isArray(val)
        ? (val as unknown[]).map((v) =>
            v && typeof v === "object" ? toCamel(v as Record<string, unknown>) : v
          )
        : val;
  }
  return result;
}

function getToken(): string | null {
  return localStorage.getItem("payflow_token");
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const isFormData = options.body instanceof FormData;

  const headers: Record<string, string> = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(!isFormData ? { "Content-Type": "application/json" } : {}),
    ...(options.headers as Record<string, string> | undefined),
  };

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers });

  if (res.status === 401) {
    // Only force-redirect if the user had a token (expired session).
    // If there was no token, the request was made from a public page (e.g. PlanContext
    // on the login screen) — redirecting would crash the login flow.
    if (token) {
      localStorage.removeItem("payflow_token");
      localStorage.removeItem("payflow_user");
      window.location.href = "/auth/login";
    }
    throw new Error("Unauthorized");
  }

  if (res.status === 204) return null as T;

  const json = await res.json();

  if (!res.ok) {
    throw new Error((json as { detail?: string }).detail ?? "Request failed");
  }

  if (Array.isArray(json)) {
    return json.map((item: Record<string, unknown>) => toCamel(item)) as T;
  }
  return toCamel(json as Record<string, unknown>) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(toSnake(body as Record<string, unknown>)) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(toSnake(body as Record<string, unknown>)) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(toSnake(body as Record<string, unknown>)) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
  upload: <T>(path: string, formData: FormData) =>
    request<T>(path, { method: "POST", body: formData }),
};
